import React from "react";
import { connect } from "react-redux";
import SidebarResizer from "../sideList/SidebarResizer";
import LinkViewPort from "../links/LinkViewPort";
import TaskList from "../sideList/TaskList";
import ReactResizeDetector from "react-resize-detector";
import TimeLineCommon from "./TimeLineCommon";
import {
  ViewPortRoot,
  TimelineContent,
  ViewPortContainer,
  TimelineRoot,
  ViewPortTitleArea,
  ViewPortMainArea,
  TasksRoot,
} from "../common/StyledComponents";
import { getGridColumns } from "../../../backend/helpers/configurationHelpers";
import { ensureStartAndEnd } from "../../../backend/helpers/dateHelpers";
import VerticalResizer from "./VerticalResizer";
import SpinnerWrapper from "../common/SpinnerWrapper";
import { RESIZER_THICKNESS, TIMELINE_MIN_HEIGHT, TASK_MIN_WIDTH } from "../../helpers/Const";
import FilterPanel from "../filters/FilterPanel";
import DataTask from "../viewport/DataTask";
import { differenceInMinutes, startOfDay } from "date-fns";
import ViewportBackground from "../viewport/ViewportBackground";
import { iRootState } from "src/rematch/store";
import { IHierarchyItem } from "src/backend/interfaces/IItem";

class TaskTimeLine extends TimeLineCommon {
  renderRows = (rowInfo: {start: number, end: number}, visibleData: IHierarchyItem[], dragUnderway: boolean, xDelta: number) => {
    const result = [];
    for (let i = rowInfo.start; i < rowInfo.end; i++) {
      const task = visibleData[i];

      const objectTypeConfig = this.props.getObjectTypeConfig(task.objectType);

      const [validStart, validEnd] = ensureStartAndEnd(task.start, task.end, startOfDay(Date.now()), task.duration, objectTypeConfig.durationUnit);
      const leftPosition = Math.round(differenceInMinutes(validStart, this.props.startDate) / 60 * this.props.hourWidth);
      const width = Math.round(differenceInMinutes(validEnd, validStart) / 60 * this.props.hourWidth);
      const minWidthAdjustment = Math.max(TASK_MIN_WIDTH - width, 0);

      result.push(
        <DataTask
          key={i}
          index={i}
          task={task}
          /*label={task.name}*/
          editable={true}
          checkedOutToSomeoneElse={task.isCheckedOut && !task.isCheckedOutToThisUser}
          color={task.color}
          left={leftPosition}
          width={width}
          minWidthAdjustment={minWidthAdjustment}
          hourWidth={this.props.hourWidth}
          dragTaskId={this.props.dragTaskId}
          dragUnderway={dragUnderway}
          dragDeadZoneReached={this.dragDeadZoneReached}
          dragType={this.props.dragType}
          onStartDrag={this.onStartDrag}
          onEndDrag={this.onEndDrag}
          xDelta={xDelta}
          zoomMode={this.props.zoomMode}
        ></DataTask>
      );
    }
    return result;
  };

  render() {
    const { visibleData, dragUnderway, rowInfo, taskListExtraHeight, xDelta, yDelta, dragTaskId, dragType } = this.getCommonRenderVars();

    const taskListProps = {
      visibleData: visibleData,
      dragUnderway: dragUnderway,
      onScroll: this.handleVerticalScroll,
      onListVerticalSizeUpdate: this.onListVerticalSizeUpdate,
      scrollTop: this.state.scrollTop,
      width: this.props.sideBarWidth,
      startRow: rowInfo.start,
      endRow: rowInfo.end,
      taskListExtraHeight: taskListExtraHeight,
      onToggleTaskExpand: this.toggleTaskExpand,
      onToggleExpandAll: this.toggleExpandAll,
      expandedTasks: this.state.expandedTasks,
      onAddSortColumn: this.addSortColumn,
      onColumnResize: this.onColumnResize,
      visibleTaskColumns: this.state.visibleTaskColumns,
      sortOrder: this.state.sortOrder,
      viewIndex: this.props.viewIndex,
    };
    const linkViewPortProps = {
      xDelta: xDelta,
      yDelta: yDelta,
      dragUnderway: dragUnderway,
      dragTaskId: dragTaskId,
      dragType: dragType,
      visibleData: visibleData,
      startDate: this.props.startDate,
      hourWidth: this.props.hourWidth,
      allData: this.props.allData,
      chartTotalWidth: this.props.chartTotalWidth,
      viewIndex: this.props.viewIndex,
      zoomMode: this.props.zoomMode,
      scrollLeft: this.props.scrollLeft,
      scrollTop: this.state.scrollTop,
    };

    let timelineHeight = this.props.timelineTotalHeight ? this.props.timelineTotalHeight : TIMELINE_MIN_HEIGHT;
    if (!this.props.isLast) {
      timelineHeight -= RESIZER_THICKNESS;
    }

    return (
      <TimelineRoot className="timeline-root">
        <TimelineContent>
          <TaskList {...taskListProps} />
          <SidebarResizer onTaskListSizing={this.props.onTaskListSizing} />
          <ViewPortMainArea height={timelineHeight}>
            <ViewPortTitleArea>
              <FilterPanel viewIndex={this.props.viewIndex} />
            </ViewPortTitleArea>
            <ViewPortRoot id={this.resizeRef} ref={this.viewPortRoot} onScroll={this.handleScroll}>
              <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={this.onResize} querySelector={`#${this.resizeRef}`}></ReactResizeDetector>
              {this.props.headerElem}
              {this.props.loading ? (
                <SpinnerWrapper />
              ) : (
                <ViewPortContainer className="viewport-container" height={visibleData.length * this.props.taskHeight}>
                  <ViewportBackground
                    zoomMode={this.props.zoomMode}
                    hourWidth={this.props.hourWidth}
                    taskCount={visibleData.length}
                    startDate={this.props.startDate}
                    endDate={this.props.endDate}
                    dragUnderway={dragUnderway}
                  />
                  <LinkViewPort {...linkViewPortProps} />
                  <TasksRoot width={this.props.chartTotalWidth} height={visibleData.length * this.props.taskHeight}>{this.renderRows(rowInfo, visibleData, dragUnderway, xDelta)}</TasksRoot>
                </ViewPortContainer>
              )}
            </ViewPortRoot>
          </ViewPortMainArea>
        </TimelineContent>
        {this.props.isLast ? null : (
          <VerticalResizer onTimelineVerticalResizerDrag={this.props.onTimelineVerticalResizerDrag} viewIndex={this.props.viewIndex} />
        )}
      </TimelineRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  taskHeight: state.UI.displaySettings.taskHeight,
  visibleColumns: getGridColumns(state.common.configuration, state.UI.displaySettings),
  getObjectTypeConfig: (objectTypeId: number) => Array.from(state.common.configuration.objectTypeConfigurations.values()).find(i => i.objectTypeId === objectTypeId),
});

export default connect(mapState, null)(TaskTimeLine);
